<template>
  <div class="bg-white rounded-lg border border-growth-50 overflow-hidden">
    <!-- Header -->
    <div class="px-4 py-3 border-b border-growth-50 bg-growth-10 flex justify-between items-center">
      <span class="text-xs font-semibold text-growth-600 uppercase tracking-wide">
        {{ isMulti ? 'ACO Peer Benchmark' : 'Peer Benchmark' }}
      </span>
      <div class="flex items-center gap-2">
        <svg v-if="loading" class="animate-spin h-3.5 w-3.5 text-growth-tangerine" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
        <span v-if="combined" class="text-xs text-growth-600">{{ combined.peer_count }} peers</span>
        <span v-if="isMulti && result" class="text-xs text-growth-600">· {{ result.aggregate.total_tins }} TINs</span>
      </div>
    </div>

    <!-- Loading (initial only) -->
    <div v-if="loading && !result" class="p-6 text-center text-growth-600">
      <svg class="animate-spin h-5 w-5 mx-auto mb-2" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
      Loading...
    </div>

    <div v-else-if="!loading && !result" class="p-6 text-center text-sm text-growth-600">
      <p>Peer benchmark unavailable</p>
      <p class="text-xs mt-1">Practice may not have sufficient tagging or cost data for peer comparison</p>
    </div>

    <div v-else-if="result" class="p-4">
      <!-- Combined percentile -->
      <div v-if="combined" class="flex items-center gap-4 mb-5 pb-5 border-b border-growth-50">
        <div class="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-lg" :class="pctColor(combinedPctl)">
          {{ Math.round(combinedPctl) }}<span class="text-xs font-normal">th</span>
        </div>
        <div>
          <div class="font-semibold text-growth-900">{{ pctLabel(combinedPctl) }}</div>
          <div class="text-sm text-growth-600">
            ${{ fmtNum(subjectPmpy) }} PMPY
            <template v-if="combined.peer_median_pmpy"> vs ${{ fmtNum(combined.peer_median_pmpy) }} median ({{ combined.vs_median_pct >= 0 ? '+' : '' }}{{ combined.vs_median_pct }}%)</template>
          </div>
        </div>
      </div>

      <!-- MULTI: Per-TIN table -->
      <template v-if="isMulti && result.per_tin">
        <div class="text-xs font-semibold text-growth-600 uppercase tracking-wide mb-3">Per-TIN Breakdown</div>
        <table class="w-full text-sm mb-4">
          <thead>
            <tr class="text-left text-xs text-growth-600 uppercase border-b border-growth-50">
              <th class="pb-2 pr-3">Practice</th>
              <th class="pb-2 pr-3 text-right">Benes</th>
              <th class="pb-2 pr-3 text-right">PMPY</th>
              <th class="pb-2 pr-3 text-right">Peer Median</th>
              <th class="pb-2 pr-3 text-center">Pctl</th>
              <th class="pb-2 text-right">Peers</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="t in result.per_tin" :key="t.tin_id"
              class="border-b border-growth-50 last:border-0 cursor-pointer hover:bg-growth-10 transition-colors"
              :class="selectedTin === t.tin_id ? 'bg-growth-tangerine/5' : ''"
              @click="$emit('select-tin', t.tin_id)">
              <td class="py-2 pr-3">
                <div class="font-medium text-growth-900 truncate max-w-xs">{{ t.name }}</div>
                <div class="text-xs text-growth-600">{{ t.tin_id }} · {{ t.benes_pct }}%</div>
              </td>
              <td class="py-2 pr-3 text-right">{{ t.benes != null ? t.benes.toLocaleString() : '—' }}</td>
              <td class="py-2 pr-3 text-right">${{ fmtNum(t.pmpy) }}</td>
              <td class="py-2 pr-3 text-right">{{ t.peer_median_pmpy ? '$' + fmtNum(t.peer_median_pmpy) : '—' }}</td>
              <td class="py-2 pr-3 text-center">
                <span v-if="t.percentile != null" class="inline-block w-10 text-xs font-semibold rounded px-1.5 py-0.5 text-white" :class="pctColor(t.percentile)">{{ Math.round(t.percentile) }}</span>
                <span v-else class="text-growth-600">—</span>
              </td>
              <td class="py-2 text-right text-growth-600">{{ t.peer_count }}</td>
            </tr>
          </tbody>
        </table>

        <!-- Roster composition (compact) -->
        <div v-if="result.tag_composition" class="text-xs text-growth-600 flex flex-wrap gap-x-4 gap-y-1">
          <div v-for="(info, dim) in result.tag_composition" :key="dim">
            <span class="font-medium">{{ info.label }}:</span>
            <span v-for="(pct, val, idx) in info.values" :key="val">
              {{ formatTag(val) }}<span v-if="pct < 100"> {{ pct }}%</span><span v-if="idx < Object.keys(info.values).length - 1">, </span>
            </span>
          </div>
        </div>
      </template>

      <!-- SINGLE: Per-dimension table with checkboxes -->
      <template v-if="!isMulti && dimensions">
        <div class="flex justify-between items-center mb-3">
          <div class="text-xs font-semibold text-growth-600 uppercase tracking-wide">By Dimension</div>
          <button v-if="dirty" @click="recompute"
            class="px-3 py-1 bg-growth-tangerine text-white rounded text-xs font-medium hover:bg-growth-tangerine-hover transition-colors">
            Recompute
          </button>
        </div>
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-xs text-growth-600 uppercase border-b border-growth-50">
              <th class="pb-2 pr-2 w-8"></th>
              <th class="pb-2 pr-3">Dimension</th>
              <th class="pb-2 pr-3">Value</th>
              <th class="pb-2 pr-3 text-right">Peers</th>
              <th class="pb-2 pr-3 text-right">Median</th>
              <th class="pb-2 pr-3 text-center">Pctl</th>
              <th class="pb-2 text-right">vs Med</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="d in dimensions" :key="d.dimension" :class="['border-b border-growth-50 last:border-0 transition-opacity', !activeDims.has(d.dimension) ? 'opacity-40' : '']">
              <td class="py-2 pr-2">
                <input type="checkbox" :checked="activeDims.has(d.dimension)" @change="toggleDim(d.dimension)"
                  class="rounded text-growth-tangerine focus:ring-growth-tangerine cursor-pointer" />
              </td>
              <td class="py-2 pr-3 text-growth-600">{{ d.label }}</td>
              <td class="py-2 pr-3"><span class="px-2 py-0.5 rounded text-xs font-medium bg-growth-10 text-growth-900">{{ formatTag(d.value) }}</span></td>
              <td class="py-2 pr-3 text-right text-growth-600">{{ d.peer_count != null ? d.peer_count.toLocaleString() : '—' }}</td>
              <td class="py-2 pr-3 text-right">${{ fmtNum(d.peer_median_pmpy) }}</td>
              <td class="py-2 pr-3 text-center">
                <span v-if="d.percentile != null" class="inline-block w-10 text-xs font-semibold rounded px-1.5 py-0.5 text-white" :class="pctColor(d.percentile)">{{ Math.round(d.percentile) }}</span>
                <span v-else class="text-growth-600">—</span>
              </td>
              <td class="py-2 text-right font-medium" :class="d.vs_median_pct != null && d.vs_median_pct <= 0 ? 'text-emerald-600' : 'text-red-500'">
                {{ d.vs_median_pct != null ? (d.vs_median_pct > 0 ? '+' : '') + d.vs_median_pct + '%' : '—' }}
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Peer list -->
        <div v-if="peerList && peerList.length > 0" class="mt-4 pt-4 border-t border-growth-50">
          <div class="text-xs font-semibold text-growth-600 uppercase tracking-wide mb-3">Matching Practices ({{ peerList.length }})</div>
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-xs text-growth-600 uppercase border-b border-growth-50">
                <th class="pb-2 pr-3">Practice</th>
                <th class="pb-2 pr-3 text-right">Benes</th>
                <th class="pb-2 pr-3 text-right">PMPY</th>
                <th class="pb-2 text-right">HCC</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="peer in sortedWithSubject" :key="peer.tin_id || 'subject'" :class="['border-b border-growth-50 last:border-0', peer.is_subject ? 'bg-growth-tangerine/10 font-semibold' : '']">
                <td class="py-1.5 pr-3">
                  <div class="truncate max-w-xs">{{ peer.name }}</div>
                  <div v-if="!peer.is_subject" class="text-xs text-growth-600">{{ peer.tin_id }}</div>
                </td>
                <td class="py-1.5 pr-3 text-right">{{ peer.benes ? peer.benes.toLocaleString() : '' }}</td>
                <td class="py-1.5 pr-3 text-right">${{ fmtNum(peer.pmpy) }}</td>
                <td class="py-1.5 text-right">{{ peer.hcc?.toFixed(3) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Tag profile -->
        <div v-if="tagProfile" class="mt-4 pt-4 border-t border-growth-50">
          <button @click="showProfile = !showProfile" class="text-xs text-growth-tangerine hover:underline">
            {{ showProfile ? 'Hide full profile' : 'Show full tag profile' }}
          </button>
          <div v-if="showProfile" class="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
            <template v-for="(val, key) in cleanProfile" :key="key">
              <div class="text-growth-600">{{ formatTag(key) }}</div>
              <div class="text-growth-900 font-medium">{{ formatTagValue(val) }}</div>
            </template>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PracticePeerBenchmark',
  props: {
    tinIds: { type: Array, default: () => [] },
    data: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    selectedTin: { type: String, default: null },
  },
  emits: ['select-tin', 'update-dimensions'],
  data() {
    return {
      result: null,
      selfLoading: false,
      showProfile: false,
      activeDims: new Set(),
      dimsInitialized: false,
      dirty: false,
    }
  },
  watch: {
    data: {
      immediate: true,
      handler(d) {
        if (d) {
          this.result = d.mode ? d : (d.single || d)
          this.initDims()
        }
      },
    },
  },
  computed: {
    isMulti() {
      return this.result?.mode === 'roster'
    },
    singleResult() {
      if (!this.result) return null
      if (this.result.mode === 'single') return this.result.single
      if (this.result.subject_pmpy != null) return this.result
      return null
    },
    combined() {
      if (this.isMulti) return this.result?.aggregate ? { peer_count: this.result.aggregate.total_benes, peer_median_pmpy: null, vs_median_pct: null, ...this.result.aggregate } : null
      return this.singleResult?.combined
    },
    combinedPctl() {
      if (this.isMulti) return this.result?.aggregate?.weighted_percentile
      return this.singleResult?.combined?.percentile
    },
    subjectPmpy() {
      if (this.isMulti) return this.result?.aggregate?.roster_pmpy
      return this.singleResult?.subject_pmpy
    },
    dimensions() {
      return this.singleResult?.dimensions
    },
    peerList() {
      return this.singleResult?.peer_list
    },
    tagProfile() {
      return this.singleResult?.tag_profile
    },
    cleanProfile() {
      if (!this.tagProfile) return {}
      const skip = ['NPI_COUNT', 'TOTAL_REVENUE', 'AVG_HCC', 'DUAL_PCT', 'EM_PCT', 'CREATED_AT',
        'MEDICAID_REVENUE', 'MEDICAID_CLAIMS', 'MEDICAID_NPI_COUNT', 'MEDICAID_SHARE',
        'MEDICAID_EM_PCT', 'MEDICAID_HOME_HEALTH_PCT', 'MEDICAID_BEHAVIORAL_PCT', 'MEDICAID_ED_PCT']
      return Object.fromEntries(Object.entries(this.tagProfile).filter(([k, v]) => v !== null && v !== false && !skip.includes(k)))
    },
    sortedWithSubject() {
      if (!this.peerList) return []
      const subject = { tin_id: 'subject', name: 'This Practice', benes: null, pmpy: this.singleResult?.subject_pmpy, hcc: this.singleResult?.subject_hcc, is_subject: true }
      const all = [...this.peerList.map(p => ({ ...p, is_subject: false })), subject]
      all.sort((a, b) => a.pmpy - b.pmpy)
      return all
    },
  },
  methods: {
    initDims() {
      if (this.dimensions && !this.dimsInitialized) {
        this.activeDims = new Set(this.dimensions.map(d => d.dimension))
        this.dimsInitialized = true
      }
    },
    toggleDim(dim) {
      if (this.activeDims.has(dim)) this.activeDims.delete(dim)
      else this.activeDims.add(dim)
      this.activeDims = new Set(this.activeDims)
      this.dirty = true
    },
    recompute() {
      this.dirty = false
      this.$emit('update-dimensions', [...this.activeDims])
    },
    fmtNum(val) { return val == null ? '—' : Math.round(val).toLocaleString() },
    formatTag(k) { return String(k).replace(/^IS_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) },
    formatTagValue(v) {
      if (typeof v === 'boolean') return v ? 'Yes' : 'No'
      if (typeof v === 'number') return v.toLocaleString()
      return String(v).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    },
    pctColor(pct) {
      if (pct == null) return 'bg-gray-400'
      if (pct <= 25) return 'bg-emerald-500'; if (pct <= 40) return 'bg-emerald-400'
      if (pct <= 60) return 'bg-amber-400'; if (pct <= 75) return 'bg-amber-500'
      return 'bg-red-400'
    },
    pctLabel(pct) {
      if (pct == null) return 'Unknown'
      if (pct <= 25) return 'Well below peer cost'; if (pct <= 40) return 'Below peer cost'
      if (pct <= 60) return 'Near peer average'; if (pct <= 75) return 'Above peer cost'
      return 'Well above peer cost'
    },
  },
}
</script>
